import { useEffect, useMemo, useState } from "react";
import { SalesBudgetView } from "@/components/blocks/budget";
import { RevisionDetailsView } from "@/components/blocks/budget/revision-details-view";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { fetchCostCentreBudgetsByProject } from "@/lib/data-service";
import type { BudgetRevision, SalesCategory } from "@/types/budget.types";
import { useProjectDetailsContext } from "../context";

export function SalesBudgetTab() {
	const ctx = useProjectDetailsContext();
	const {
		project,
		numericProjectId,
		budgetData,
		budgetLoading,
		budgetError,
		fixedFeeInvoices,
		fixedFeeInvoicedToDate,
		setEditSalesBudgetPageOpen,
		setBudgetExportModalOpen,
		setActiveBudgetType,
		showSalesRevisionHistory,
		setShowSalesRevisionHistory,
		salesRevisions,
		setSalesLoadedRevision,
		setSalesMakeActiveRevision,
		setSalesMakeActiveSource,
		setIsSalesNewRevision,
		effectiveCompletionPercent,
		budgetPermissions,
		costCentres,
	} = ctx;

	// In Cost Centre mode, fetch CC sales budget data from API.
	// Re-fetches whenever budgetData changes (e.g. after a save triggers refetchBudget).
	// Uses a cancelled-flag cleanup so that a dependency change (budgetData after save)
	// always triggers a fresh fetch — the previous in-flight request is discarded.
	const [ccBudgetEntries, setCcBudgetEntries] = useState<
		Record<number, Record<string, number>>
	>({});
	// biome-ignore lint/correctness/useExhaustiveDependencies: budgetData is intentionally included to trigger re-fetch after save
	useEffect(() => {
		if (!budgetPermissions.isCostCentreMode || !costCentres?.length) return;
		let cancelled = false;
		fetchCostCentreBudgetsByProject(numericProjectId, "sales")
			.then((entries) => {
				if (cancelled) return;
				const map: Record<number, Record<string, number>> = {};
				for (const entry of entries) {
					if (entry.monthlyBudgets) {
						const monthly =
							typeof entry.monthlyBudgets === "string"
								? (JSON.parse(entry.monthlyBudgets) as Record<string, number>)
								: entry.monthlyBudgets;
						map[entry.costCentreId] = monthly;
					}
				}
				setCcBudgetEntries(map);
			})
			.catch(() => {
				/* fall back to empty */
			});
		return () => {
			cancelled = true;
		};
	}, [
		budgetPermissions.isCostCentreMode,
		costCentres,
		numericProjectId,
		budgetData,
	]);

	const ccModeData = useMemo(() => {
		if (!budgetPermissions.isCostCentreMode || !costCentres?.length || !project)
			return null;
		const categories: SalesCategory[] = costCentres.map((cc) => {
			const monthly = ccBudgetEntries[cc.id ?? 0];
			const total = monthly
				? Object.values(monthly).reduce((s, v) => s + (v ?? 0), 0)
				: 0;
			return {
				id: cc.id ?? 0,
				name: `${cc.code} – ${cc.name}`,
				type: "labour" as const,
				budget: total,
				actual: 0,
				remaining: total,
				percentBilled: 0,
				scope: "project" as const,
			};
		});
		const totalBudget = categories.reduce((s, c) => s + c.budget, 0);
		return {
			salesBudget: {
				...(budgetData?.salesBudget ?? {
					projectId: numericProjectId,
					projectType: project.type,
				}),
				categories,
				totals: {
					budget: totalBudget,
					actual: 0,
					remaining: totalBudget,
					percentBilled: 0,
				},
			},
		};
	}, [
		budgetPermissions.isCostCentreMode,
		costCentres,
		numericProjectId,
		budgetData?.salesBudget,
		project,
		ccBudgetEntries,
	]);

	const salesBudgetData = budgetPermissions.isCostCentreMode
		? ccModeData?.salesBudget
		: budgetData?.salesBudget;

	if (!project) return null;

	return (
		<>
			{budgetLoading ? (
				<div className="flex items-center justify-center h-96">
					<div className="text-center">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
						<p className="text-muted-foreground">Loading budget data...</p>
					</div>
				</div>
			) : budgetError ? (
				<div className="flex items-center justify-center h-96">
					<div className="text-center space-y-4">
						<div className="text-destructive mb-2">
							Error loading budget data
						</div>
						<p className="text-sm text-muted-foreground">{budgetError}</p>
					</div>
				</div>
			) : (
				<SalesBudgetView
					projectId={numericProjectId}
					data={salesBudgetData}
					costBudget={budgetData?.costBudget}
					projectType={project.type}
					billingTriggerType={project.billingTriggerType}
					completionPercent={
						project.type === "fixed-price"
							? effectiveCompletionPercent
							: undefined
					}
					completionPercentMode={
						project.type === "fixed-price"
							? (project.completionPercentMode ?? "based-on-time")
							: undefined
					}
					completionPercentOverriddenBy={
						project.type === "fixed-price"
							? project.completionPercentOverriddenBy
							: undefined
					}
					invoicedToDate={
						project.type === "fixed-price" ? fixedFeeInvoicedToDate : undefined
					}
					milestoneInvoices={
						project.type === "fixed-price" ? fixedFeeInvoices : undefined
					}
					onCreateBudget={() => {
						setIsSalesNewRevision(false);
						setEditSalesBudgetPageOpen(true);
					}}
					onEdit={() => {
						setIsSalesNewRevision(false);
						setEditSalesBudgetPageOpen(true);
					}}
					onExport={() => {
						setActiveBudgetType("sales");
						setBudgetExportModalOpen(true);
					}}
					onHistory={() => {
						setShowSalesRevisionHistory(true);
					}}
				/>
			)}

			{/* Sales Budget Revision History Sheet — rendered outside the
          loading/error/data conditional so it stays mounted across
          budget refetch cycles and Radix Dialog state is preserved. */}
			<Sheet
				open={showSalesRevisionHistory}
				onOpenChange={setShowSalesRevisionHistory}
			>
				<SheetContent
					side="right"
					className="w-full sm:max-w-4xl overflow-y-auto"
				>
					<SheetHeader className="px-6 pt-6">
						<SheetTitle>Sales Budget Revision History</SheetTitle>
						<SheetDescription>
							View, compare and restore previous versions of the sales budget.
						</SheetDescription>
					</SheetHeader>
					<div className="mt-6 px-6 pb-6">
						<RevisionDetailsView
							budgetLayer="sales"
							revisions={salesRevisions}
							projectName={project?.name ?? ""}
							onViewRevision={(id) => {
								const rev = salesRevisions.find(
									(r: BudgetRevision) =>
										r.id === Number(id) || r.revisionNumber === Number(id),
								);
								if (rev) {
									setSalesLoadedRevision(rev);
									setIsSalesNewRevision(false);
									setEditSalesBudgetPageOpen(true);
								}
								setShowSalesRevisionHistory(false);
							}}
							onRestore={(id) => {
								const rev = salesRevisions.find(
									(r: BudgetRevision) => r.id === Number(id),
								);
								if (rev) {
									setSalesMakeActiveSource("restore");
									setSalesMakeActiveRevision(rev);
								}
							}}
							onMakeActive={(id) => {
								const rev = salesRevisions.find(
									(r: BudgetRevision) => r.id === Number(id),
								);
								if (rev) {
									setSalesMakeActiveSource("make-active");
									setSalesMakeActiveRevision(rev);
								}
							}}
							onNewRevision={() => {
								setShowSalesRevisionHistory(false);
								setSalesLoadedRevision(null);
								setIsSalesNewRevision(true);
								setEditSalesBudgetPageOpen(true);
							}}
							onDuplicate={(id) => {
								const rev = salesRevisions.find(
									(r: BudgetRevision) =>
										r.id === Number(id) || r.revisionNumber === Number(id),
								);
								if (rev) {
									setShowSalesRevisionHistory(false);
									setSalesLoadedRevision(rev);
									setIsSalesNewRevision(false);
									setEditSalesBudgetPageOpen(true);
								}
							}}
						/>
					</div>
				</SheetContent>
			</Sheet>
		</>
	);
}
